// @flow

import React from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Row, Col } from '@riseart/antd-provider';
import { Heading, Image, Link, Paragraph, MediaQuery } from '@riseart/common';
import { Section, SectionContent, Wrapper } from '@riseart/layout';
import { StarIcon } from '@riseart/icons';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { Picture, generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';
import { ReviewsLogo } from 'shared_components/arts/reviews/Logo';

import { raScreenLg } from '@riseart/antd-provider/dist/website/variables.less';
import {
  cmsModuleTestimonialsCls,
  cmsModuleTestimonialsTypeDefaultCls,
  cmsModuleTestimonialsTypeHomeCls,
  cmsModuleTestimonialsTypeSimpleCls,
  cmsModuleTestimonialsContainerCls,
  cmsModuleTestimonialsItemCls,
  cmsModuleTestimonialsItemLargeCls,
  cmsModuleTestimonialsQuoteCls,
  cmsModuleTestimonialsDetailsCls,
  cmsModuleTestimonialsScoreCls,
  cmsModuleTestimonialsAuthorCls,
  cmsModuleTestimonialsLinkCls,
  cmsModuleTestimonialReviewsDetailsCls,
  cmsModuleTestimonialReviewsScoreCls,
  cmsModuleTestimonialReviewsAuthorCls,
  cmsModuleTestimonialReviewsFooterCls,
  cmsModuleTestimonialReviewsLogoCls,
} from 'shared_components/cms/modules/testimonial/Testimonials.less';

type Props = {
  title: string,
  type: string,
  items: Array<{
    title: string,
    text?: string,
    author?: string,
    score?: number,
    artwork?: Object,
    image?: string,
  }>,
};

export const CMS_TESTIMONIALS_FALLBACK_CONFIG = { common: { height: 430 } };

/**
 * CmsModuleTestimonials
 *
 * @param {Props} props
 */
export const CmsModuleTestimonials = ({ title, type = 'home', items }: Props): Node => {
  const isSimpleType = type === 'simple';
  const isHomeType = type === 'home';

  if (!items || !items.length) {
    return null;
  }

  return (
    <Section
      className={classNames(cmsModuleTestimonialsCls, {
        [cmsModuleTestimonialsTypeDefaultCls]: !type || type === 'default',
        [cmsModuleTestimonialsTypeHomeCls]: isHomeType,
        [cmsModuleTestimonialsTypeSimpleCls]: isSimpleType,
      })}
    >
      <Wrapper>
        <SectionContent
          title={title}
          headingProps={{ tag: 'h2', level: 3, align: 'center', uppercase: true }}
        >
          <MediaQuery minWidth={raScreenLg}>
            {(isDesktop) => (
              <Row
                className={cmsModuleTestimonialsContainerCls}
                gutter={isDesktop ? 46 : 18}
                {...(isHomeType ? { align: isDesktop ? 'bottom' : 'middle' } : null)}
              >
                {items.map(({ title, text, author, score, artwork, image }, idx) => {
                  return (
                    <Col xs={idx !== 1 ? 0 : 24} sm={idx === 0 ? 0 : 12} lg={8} key={title || idx}>
                      <article
                        className={classNames(
                          cmsModuleTestimonialsItemCls,
                          isHomeType && idx === 1 && cmsModuleTestimonialsItemLargeCls,
                        )}
                      >
                        <span className={cmsModuleTestimonialsQuoteCls}>“</span>
                        {title ? <Heading tag="h3">{title}</Heading> : null}
                        {text ? <Paragraph>{text}</Paragraph> : null}
                        {isHomeType && artwork ? (
                          <div>
                            <FormattedMessage
                              id="components.art.artwork_by_artist"
                              values={{ title: artwork.title, artist: artwork.artist }}
                            >
                              {(artworkByArtistLabel: string) => {
                                const PictureComponent = artwork.file ? (
                                  <Picture
                                    type="art.imageTestimonials"
                                    artDirectionKey="art.main"
                                    image={artwork.file}
                                    alt={artworkByArtistLabel}
                                  />
                                ) : null;
                                return (
                                  <Row gutter={16}>
                                    <Col span={8}>
                                      {artwork.url ? (
                                        <Link
                                          href={artwork.url}
                                          title={artworkByArtistLabel}
                                          {...(artwork.external
                                            ? { external: true, target: '_blank' }
                                            : null)}
                                        >
                                          {PictureComponent}
                                        </Link>
                                      ) : (
                                        PictureComponent
                                      )}
                                    </Col>
                                    <Col span={16} className={cmsModuleTestimonialsDetailsCls}>
                                      <div>
                                        <span className={cmsModuleTestimonialsAuthorCls}>
                                          {author}
                                        </span>
                                        <span className={cmsModuleTestimonialsScoreCls}>
                                          {Array.apply(0, Array(score)).map(
                                            (item, starIconIndex) => (
                                              <StarIcon key={starIconIndex} />
                                            ),
                                          )}
                                        </span>
                                      </div>
                                      <div>{artworkByArtistLabel}</div>
                                    </Col>
                                  </Row>
                                );
                              }}
                            </FormattedMessage>
                            <div className={cmsModuleTestimonialsLinkCls}>
                              <FormattedMessage id="common.read_more">
                                {(readMore: string) => (
                                  <Link
                                    type="light"
                                    external
                                    href={LocalizedConfig.get('externalUrls.companyReviews')}
                                    title={readMore}
                                    target="_blank"
                                  >
                                    {readMore}
                                  </Link>
                                )}
                              </FormattedMessage>
                            </div>
                          </div>
                        ) : (
                          <div className={cmsModuleTestimonialReviewsDetailsCls}>
                            {author ? (
                              <div className={cmsModuleTestimonialReviewsAuthorCls}>{author}</div>
                            ) : null}
                            {score ? (
                              <div className={cmsModuleTestimonialReviewsScoreCls}>
                                {Array.apply(0, Array(score)).map((item, starIconIndex) => (
                                  <StarIcon key={starIconIndex} />
                                ))}
                              </div>
                            ) : null}
                            {image ? <Image src={generateSrcForCDN(image)} alt={title} /> : null}
                          </div>
                        )}
                      </article>
                    </Col>
                  );
                })}
              </Row>
            )}
          </MediaQuery>
          {!isHomeType && !isSimpleType ? (
            <footer className={cmsModuleTestimonialReviewsFooterCls}>
              <FormattedMessage id="components.cms.modules.reviews.readAll">
                {(label: string) => (
                  <Link
                    external
                    href={LocalizedConfig.get('externalUrls.companyReviews')}
                    title={label}
                    target="_blank"
                  >
                    <strong>{label}:</strong>
                    <span className={cmsModuleTestimonialReviewsLogoCls}>
                      <ReviewsLogo />
                    </span>
                  </Link>
                )}
              </FormattedMessage>
            </footer>
          ) : null}
        </SectionContent>
      </Wrapper>
    </Section>
  );
};
